import { CompactInput, PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import React from 'react';
import styled from 'styled-components';
import { useIsSceneLook } from './propertiesPanelLook';
import { PropertiesPanelNumberField } from './propertiesPanelNumberField';
import { panel } from './panelTheme';
import { LinkIcon } from 'src/components/widgets/icons/scene';
import { ZTooltip } from 'src/components/widgets/ztooltip';

const NumberInput = styled(CompactInput)`
    min-width: 50px;
    max-width: 50px;
    text-align: center;
`;

const CoordContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
`;

// PIÙ NUMERI COME UN OGGETTO SOLO: i tre assi di una trasformazione, i due di una coordinata di
// texture. Non sono tre campi vicini, sono un valore in tre parti — e si leggono insieme.
//
// TANTE TRACCE QUANTI SONO I CAMPI, e non tre fisse: con `repeat(3, 1fr)` una coppia occuperebbe
// due terzi della riga lasciando un terzo di vuoto a destra, cioè non finirebbe dove finisce il
// campo singolo della riga sotto. Le tracce le conta il browser; chi monta la riga passa i valori.
const Triple = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 4px;
    min-width: 0;
`;

type Coord = {
    id: string;
    label: React.ReactNode;
    value: number;
    min?: number;
    max?: number;
    step?: number;
    /** Il suffisso dentro il campo: il grado di una rotazione. */
    unit?: string;
    /** Spento solo questo asse: una coordinata che c'è ma non si sceglie, come il centro di un'orbita. */
    disabled?: boolean;
    /** L'evento c'è solo quando il valore arriva da un campo: dallo stepper non c'è nulla da passare. */
    onChange?: (newValue: number, e?: React.ChangeEvent<HTMLInputElement>) => void;
};
// LA CATENA LEGA I TRE ASSI. Accesa, muovere un asse muove gli altri due: è il gesto con cui
// si ingrandisce una cosa senza deformarla, ed è il motivo per cui sta ACCANTO AL NOME e non
// in fondo alla riga — riguarda tutti e tre i campi, non il terzo.
export type PropertiesPanelPointPropertyProps = {
    id: string;
    label: React.ReactNode;
    coords: Coord[];
    /** Quando c'è, accanto al nome compare la catena che lega gli assi fra loro. */
    link?: { active: boolean; onToggle: (active: boolean) => void; title?: string };
    disabled?: boolean;
};

const Labelled = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const Chain = styled.button<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: ${(props) => (props.$active ? panel.field : 'transparent')};
    color: ${(props) => (props.$active ? panel.accent : panel.inkMuted)};
    cursor: pointer;

    svg {
        width: 13px;
        height: 13px;
    }

    &:hover {
        color: ${panel.accent};
    }
`;

// La lettera di un asse porta il colore del suo gizmo — la stessa convenzione del 3D, in tono da
// etichetta. Vale solo per x/y/z: una coordinata che si chiama altrimenti resta neutra.
const axisOf = (label: React.ReactNode): 'x' | 'y' | 'z' | undefined => {
    const letter = typeof label === 'string' ? label.trim().toLowerCase() : '';
    return letter === 'x' || letter === 'y' || letter === 'z' ? letter : undefined;
};

export const PropertiesPanelPointProperty: React.FC<PropertiesPanelPointPropertyProps> = ({ id, label, disabled, coords, link }) => {
    const scene = useIsSceneLook();

    if (scene) {
        return (
            <PropertiesPanelProperty narrow>
                <Labelled>
                    <PropertiesPanelLabel htmlFor={`${id}-${coords[0]?.id}`}>{label}</PropertiesPanelLabel>
                    {link && (
                        <ZTooltip title={link.title} position='top'>
                            <Chain
                                type='button'
                                $active={link.active}
                                aria-pressed={link.active}
                                aria-label={link.title}
                                disabled={disabled}
                                onClick={() => link.onToggle(!link.active)}
                            >
                                <LinkIcon />
                            </Chain>
                        </ZTooltip>
                    )}
                </Labelled>
                <Triple>
                    {coords.map((coord) => (
                        <PropertiesPanelNumberField
                            key={coord.id}
                            id={`${id}-${coord.id}`}
                            value={coord.value}
                            min={coord.min}
                            max={coord.max}
                            step={coord.step}
                            unit={coord.unit}
                            axis={axisOf(coord.label)}
                            full
                            ariaLabel={typeof coord.label === 'string' ? coord.label : undefined}
                            disabled={disabled || coord.disabled}
                            onChange={(next) => coord.onChange?.(next)}
                        />
                    ))}
                </Triple>
            </PropertiesPanelProperty>
        );
    }

    return (
        <PropertiesPanelProperty>
            <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
            <PropertiesPanelControl>
                {coords.map((coord) => (
                    <CoordContainer key={coord.id}>
                        <label htmlFor={`$${id}-${coord.id}-input`}>{coord.label}</label>
                        <NumberInput
                            id={`$${id}-${coord.id}-input`}
                            type={'number'}
                            min={coord.min}
                            max={coord.max}
                            step={coord.step}
                            value={coord.value}
                            disabled={disabled || coord.disabled}
                            onChange={(e) => {
                                if (!isNaN(e.target.valueAsNumber)) {
                                    coord.onChange?.(e.target.valueAsNumber, e);
                                }
                            }}
                        />
                    </CoordContainer>
                ))}
            </PropertiesPanelControl>
        </PropertiesPanelProperty>
    );
};
